import { Injectable, computed, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { QueryClient } from '@tanstack/angular-query-experimental';

import {
  MAIN_MENU_STATE_DEFAULTS,
  injectMainMenuStateQuery,
  injectUpdateMainMenuState,
  mainMenuStateContext,
  mainMenuStateStorageKey,
  normalizeMainMenuState,
  viewPresetQueryKey,
} from 'src/app/api/queries/view-preset.queries';
import { AppState } from 'src/app/reducers';
import { selectUser } from '../reducers';

/**
 * The main menu state per user (`ov-main-menu`, WAP-1706): pinned mode and the
 * collapsed header groups. The query cache is the state; every toggle patches it,
 * mirrors it to localStorage and posts it. A `storage` event from another tab of
 * the same browser patches the cache without a request.
 */
@Injectable({ providedIn: 'root' })
export class MainMenuStateService {
  private readonly store = inject(Store<AppState>);
  private readonly queryClient = inject(QueryClient);

  private readonly user = this.store.selectSignal(selectUser);
  private readonly userId = computed(() => this.user()?.id ?? null);

  private readonly query = injectMainMenuStateQuery(this.userId, () => ({
    ...MAIN_MENU_STATE_DEFAULTS,
    pinnedMode: this.user()?.menu_state_open ? 'expanded' : 'small',
  }));
  private readonly update = injectUpdateMainMenuState();

  readonly state = computed(
    () => this.query.data() ?? MAIN_MENU_STATE_DEFAULTS,
  );
  readonly isExpanded = computed(() => this.state().pinnedMode === 'expanded');
  private readonly collapsedGroups = computed(
    () => new Set(this.state().collapsedGroups),
  );

  constructor() {
    window.addEventListener('storage', (event) => this.onStorage(event));
  }

  isGroupCollapsed(key: string | undefined): boolean {
    return !!key && this.collapsedGroups().has(key);
  }

  togglePinnedMode(): void {
    const userId = this.userId();
    if (!userId) return;

    this.update(userId, {
      pinnedMode: this.isExpanded() ? 'small' : 'expanded',
    });
  }

  toggleGroup(key: string): void {
    const userId = this.userId();
    if (!userId) return;

    const collapsed = this.collapsedGroups();
    const collapsedGroups = collapsed.has(key)
      ? [...collapsed].filter((group) => group !== key)
      : [...collapsed, key];
    this.update(userId, { collapsedGroups });
  }

  private onStorage(event: StorageEvent): void {
    const userId = this.userId();
    if (
      !userId ||
      event.key !== mainMenuStateStorageKey(userId) ||
      !event.newValue
    ) {
      return;
    }

    try {
      this.queryClient.setQueryData(
        viewPresetQueryKey(mainMenuStateContext(userId)),
        normalizeMainMenuState(JSON.parse(event.newValue)),
      );
    } catch {
      // A foreign or corrupt value: the next fetch wins.
    }
  }
}
